import * as React from "react";
import styled from "styled-components";
import { PDFDownloadLink } from "@react-pdf/renderer";

import { CertificatePdf } from "~/components";
import { subscribeNewsletter } from "~/util";
import { useGlobalUserState } from "~/state";

import bikeImg from "../assets/gfx/bike-in-the-office.jpg";

export const ProgramFinish: React.FC<{}> = () => {
  const { firstName, lastName, email, state } = useGlobalUserState();
  const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
  const [subscribeStatus, setSubscribeStatus] = React.useState<
    "success" | "failure" | null
  >(null);

  const handleSubscribe = () => {
    setSubmitting(true);
    subscribeNewsletter({
      firstName,
      lastName,
      email,
      state: state || "WA",
      isOverEighteen: true,
    })
      .then(() => setSubscribeStatus("success"))
      .catch((err) => setSubscribeStatus("failure"))
      .finally(() => setSubmitting(false));
  };

  return (
    <FullPage>
      <PageHeader>
        <div className="bg-overlay-blueberry-dark text-center p-5">
          <div className="p-5">
            <PageTitle className="text-gold">
              Congratulations on successfully completing the Workplace Health
              Coordinator Training!
            </PageTitle>
          </div>
        </div>
      </PageHeader>

      <div className="row">
        <div className="col-md-10 col-lg-8 mx-auto d-flex flex-column">
          <p className="lead d-block text-center text-primary mb-4">
            Why not put your training into action straight away by downloading
            our toolkit below.
          </p>
          <p className="d-block text-center">
            Don’t forget to download your certificate of completion too!
          </p>
          <div className="d-flex justify-content-center mb-5">
            <a
              href="https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf"
              download="Toolkit for a healthier workplace"
              title="Download toolkit for a healthier workplace"
              className="d-block btn btn-primary"
            >
              Download toolkit for a healthier workplace
            </a>
            <PDFDownloadLink
              document={
                <CertificatePdf
                  name={`${firstName} ${lastName}`}
                  completedDate={Date.now()}
                />
              }
              className="d-block btn btn-primary ml-3"
              fileName="Healthy Workplace Training Certificate.pdf"
            >
              Download your certificate
            </PDFDownloadLink>
          </div>
          <p className="text-center mb-4">
            You can find more resources at our healthy workplaces hub.
          </p>
        </div>
      </div>
      <section className="row">
        <div className="col-12 col-lg-6 mb-3 mb-lg-0">
          <div className="h-100 training-header">
            <div className="h-100 bg-overlay-blueberry text-center px-4 py-5">
              <div className="h-100 d-flex flex-column justify-content-between mx-auto">
                <h1 className="text-gold" style={{ fontSize: "2rem" }}>
                  Workplace Hub
                </h1>
                <h2 className="h3 text-white mb-3">
                  Australians spend about a third of their lives at work, making
                  the workplace an ideal setting for promoting health and
                  wellbeing. Find out how you can help to create a healthy
                  workplace.
                </h2>
                <div>
                  <a
                    href="/health-professionals/workplaces"
                    className="btn btn-secondary"
                  >
                    Get started
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="h-100 subscribe-header">
            <div className="h-100 bg-overlay-blueberry text-center px-4 py-5">
              <div className="h-100 d-flex flex-column justify-content-between mx-auto">
                <h1 className="text-warning" style={{ fontSize: "2rem" }}>
                  Subscribe to our newsletter
                </h1>
                <div className="text-white">
                  <h2 className="h3 d-inline-block my-3">
                    Keep up to date with the latest healthy lifestyle news,
                    information, competitions and events by subscribing to our
                    newsletter. We’ll send you healthy tips about once a month
                    and you can opt out anytime.
                  </h2>
                  <p className="mb-4">
                    By subscribing to the newsletter, I accept LiveLighter's{" "}
                    <a href="/privacy" className="text-reset text-underline">
                      privacy note and collection statement
                    </a>
                    .
                  </p>
                </div>
                <div className="text-center">
                  <button
                    className="btn btn-warning"
                    onClick={handleSubscribe}
                    disabled={subscribeStatus === "success" || isSubmitting}
                  >
                    Subscribe
                  </button>
                  {subscribeStatus === "success" ? (
                    <div
                      className="alert alert-success text-left mt-3"
                      role="alert"
                    >
                      You have successfully subscribed!
                    </div>
                  ) : subscribeStatus === "failure" ? (
                    <div
                      className="alert alert-danger text-left mt-3"
                      role="alert"
                    >
                      Subscription failed, please try again later.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </FullPage>
  );
};

const FullPage = styled.div`
  margin: 0 -2rem 2rem;
`;

const PageHeader = styled.section`
  background-image: url(${bikeImg});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2em;
  line-height: 1.5;
`;
